/**
 * Microsoft Advertising Adapter (Story 19.1 + 19.2)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 *
 * Story 19.1 wired OAuth + the universal Developer Token; Story 19.2 wires the
 * Reporting Service v13 (async Submit→Poll→Download→Unzip→Parse) and populates
 * the metric/dimension catalogues from
 * `_bmad/bmm/data/integration-metrics/microsoft-ads.md` (65 metrics, 85
 * dimensions). Widget presets and fixture data are out of scope — those land
 * in Story 19.3.
 *
 * File-split rationale (Reviewer C concern, story 19.2 dev notes):
 *   - microsoft-ads.ts        — adapter + metric/dimension catalogues
 *   - microsoft/reporting.ts  — Submit/Poll/Download/Unzip lifecycle
 *   - microsoft/report-builders.ts — column lists + body composer
 *   - microsoft/transformers.ts — CSV → typed records
 *   - microsoft/unzip.ts      — ZIP extractor (no external deps)
 *
 * google-ads.ts ships everything in one 1,200-line file; we deliberately
 * diverge to keep each MS file <500 lines per the build-to-sell directive
 * ("a senior developer should understand within 60 seconds of reading").
 *
 * ADRs:
 *   - ADR-013 (Adapter Pattern): plain object implementing IntegrationAdapter
 *   - ADR-014 (API Versioning): `microsoftAds` lives in `API_VERSIONS`
 *   - ADR-016 (Aggressive Caching): 1h soft TTL via PLATFORM_CACHE_TTL
 *   - ADR-017 (Retry Logic): all HTTP calls flow through `safeFetch`
 *   - ADR-018 (Rate Limiter): 12,000 calls/min/account ceiling — well above
 *     our worst case ~256 calls per fetchMetrics
 *   - ADR-020 (Token Lifecycle): refresh tokens may rotate — adapter persists
 *     rotated tokens
 */

import type {
  AccountInfo,
  AdapterContext,
  AdapterErrorCode,
  AdapterMetrics,
  AdapterResult,
  DimensionDefinition,
  IntegrationAdapter,
  MetricDefinition,
  OAuthCredentials,
  RefreshedTokens,
} from "../adapter";
import { adapterErr, adapterOk } from "../adapter";
import { API_VERSIONS } from "../config";
import { sanitizeErrorMessage } from "../safe-fetch";
import {
  fetchMicrosoftAdsAccountsSearch,
  fetchMicrosoftAdsUserInfo,
} from "./api";
import { MicrosoftAdsApiCallError, microsoftAdsErrorTypeToAdapterCode } from "./errors";
import {
  refreshMicrosoftAdsToken,
  MICROSOFT_ADS_REFRESH_TOKEN_LIFETIME_MS,
  MICROSOFT_ADS_SCOPES,
} from "./oauth";
import {
  COLUMNS_BY_REPORT_TYPE,
  MAX_DATE_RANGE_DAYS,
  buildSubmitBody,
} from "./report-builders";
import {
  REPORT_TYPE_TO_API,
  type MicrosoftAdsReportType,
  downloadAndExtractCsv,
  parseMicrosoftAdsCsv,
  pollMicrosoftAdsReportUntilDone,
  submitMicrosoftAdsReport,
} from "./reporting";
import { TRANSFORMERS } from "./transformers";

// ============================================================================
// Constants
// ============================================================================

/** Hard cap on a single fetchMetrics call to bound peak memory (Reviewer B / Pre-mortem F2). */
export const MAX_FETCHMETRICS_DAYS = 90;

/**
 * The 8 report types Story 19.2 exercises in parallel via Promise.all.
 * Order matches the documented kebab-cased discriminator on each record's
 * `_reportType` field.
 *
 * Story 19.3 (Round 5 hindsight reflection 1): exported as a runtime tuple
 * so the fixture generator can cross-check live emissions against the same
 * source-of-truth. The runtime tuple stays in lockstep with the existing
 * `MicrosoftAdsReportType` union (declared in `reporting.ts`) via the
 * `_ReportTypeContractCheck` compile-time assertion just below — adding a
 * 9th variant to either side without the other fails the build with a
 * type-equality error.
 *
 * Fixture-side runtime tests in `fixtures/generators/microsoft-ads.test.ts`
 * additionally enforce that every emitted `_reportType` value is a member
 * of this tuple.
 */
export const MICROSOFT_ADS_REPORT_TYPES = [
  "overview",
  "campaigns",
  "adGroups",
  "keywords",
  "searchQueries",
  "shopping",
  "ageGender",
  "geo",
] as const;

/**
 * Compile-time guarantee that the runtime tuple `MICROSOFT_ADS_REPORT_TYPES`
 * stays in sync with `MicrosoftAdsReportType` (the union literal in
 * `reporting.ts`). If a future story adds a 9th variant to one without the
 * other, TypeScript fails this assignment with `Type ... is not assignable
 * to type ...` and stops the build. Round 1 review M2.
 */
type _ReportTypeContractCheck = [
  // Each runtime value must be a member of the union literal.
  (typeof MICROSOFT_ADS_REPORT_TYPES)[number] extends MicrosoftAdsReportType ? true : false,
  // Each union literal member must exist in the runtime tuple.
  MicrosoftAdsReportType extends (typeof MICROSOFT_ADS_REPORT_TYPES)[number] ? true : false,
];
const _reportTypeContract: _ReportTypeContractCheck = [true, true];
void _reportTypeContract;

const REPORT_TYPES: ReadonlyArray<MicrosoftAdsReportType> =
  MICROSOFT_ADS_REPORT_TYPES;

/**
 * Per-pipeline error → severity classifier. Fatal errors abort the entire
 * fetchMetrics; non-fatal errors emit an empty `records` slice + a `warnings`
 * entry.
 *
 * - token_expired / token_invalid → re-auth required by user (Story 4.20 wrapper takes over)
 * - permission_denied / api_not_enabled → developer-side configuration; user-actionable
 * - rate_limited → AC #12 says the queue consumer retries the whole fetchMetrics
 * - provider_unavailable → 5xx from Microsoft; same retry-the-whole-thing logic
 *
 * Non-fatal codes (`invalid_response`, `not_found`, `network_error`) leave the
 * affected report's records as `[]` and add a warnings entry — they typically
 * mean the report type doesn't apply to this account (shopping on a non-
 * shopping account, demographics under threshold).
 */
const FATAL_ERROR_CODES: ReadonlySet<AdapterErrorCode> = new Set([
  "token_expired",
  "token_invalid",
  "permission_denied",
  "api_not_enabled",
  "rate_limited",
  "provider_unavailable",
]);

// ============================================================================
// MICROSOFT_ADS_METRICS — 65 entries pinned to integration-metrics/microsoft-ads.md
// ============================================================================

/**
 * Helper that names a metric definition's fields without repeating the
 * platform-wide `compatibleWidgets` defaults — keeps the array readable.
 */
const M = (
  key: string,
  label: string,
  type: MetricDefinition["type"],
  category: string,
  dataType: NonNullable<MetricDefinition["dataType"]>,
  compatibleWidgets: string[]
): MetricDefinition => ({ key, label, type, category, dataType, compatibleWidgets });

const MICROSOFT_ADS_METRICS_BASE: MetricDefinition[] = [
  // Delivery (2)
  M("impressions", "Impressions", "number", "delivery", "Integer", ["kpi", "line-chart", "bar-chart", "data-table"]),
  M("dailySpend", "Daily Spend", "currency", "delivery", "Currency", ["kpi", "line-chart"]),
  // Engagement (2)
  M("clicks", "Clicks", "number", "engagement", "Integer", ["kpi", "line-chart", "bar-chart", "data-table"]),
  M("ctr", "Click-Through Rate", "percentage", "engagement", "Percentage", ["kpi", "gauge"]),
  // Spend & Efficiency (4)
  M("spend", "Spend", "currency", "spend", "Currency", ["kpi", "line-chart", "bar-chart"]),
  M("averageCpc", "Average CPC", "currency", "spend", "Currency", ["kpi", "line-chart"]),
  M("averageCpm", "Average CPM", "currency", "spend", "Currency", ["kpi"]),
  M("costPerConversion", "Cost Per Conversion", "currency", "spend", "Currency", ["kpi"]),
  // Conversions Qualified (9)
  M("conversionsQualified", "Conversions (Qualified)", "number", "conversions", "Decimal", ["kpi", "line-chart"]),
  M("allConversionsQualified", "All Conversions (Qualified)", "number", "conversions", "Decimal", ["kpi", "line-chart"]),
  M("conversionRate", "Conversion Rate", "percentage", "conversions", "Percentage", ["kpi", "gauge"]),
  M("allConversionRate", "All Conversion Rate", "percentage", "conversions", "Percentage", ["kpi", "gauge"]),
  M("allCostPerConversion", "All Cost Per Conversion", "currency", "conversions", "Currency", ["kpi"]),
  M("viewThroughConversions", "View-Through Conversions", "number", "conversions", "Integer", ["kpi"]),
  M("viewThroughConversionRate", "View-Through Conversion Rate", "percentage", "conversions", "Decimal", ["kpi", "gauge"]),
  M("viewThroughCostPerConversion", "View-Through Cost Per Conversion", "currency", "conversions", "Currency", ["kpi"]),
  M("numberOfConversions", "Number Of Conversions", "number", "conversions", "Integer", ["kpi"]),
  // Revenue & ROAS (6)
  M("returnOnAdSpend", "Return On Ad Spend", "number", "revenue", "Ratio", ["kpi", "gauge"]),
  M("allReturnOnAdSpend", "All Return On Ad Spend", "number", "revenue", "Ratio", ["kpi", "gauge"]),
  M("revenue", "Revenue", "currency", "revenue", "Currency", ["kpi", "line-chart"]),
  M("allRevenue", "All Revenue", "currency", "revenue", "Currency", ["kpi", "line-chart"]),
  M("revenuePerConversion", "Revenue Per Conversion", "currency", "revenue", "Currency", ["kpi"]),
  M("allRevenuePerConversion", "All Revenue Per Conversion", "currency", "revenue", "Currency", ["kpi"]),
  // App (5)
  M("installs", "Installs", "number", "app", "Integer", ["kpi"]),
  M("numberOfInstalls", "Number Of Installs", "number", "app", "Integer", ["kpi"]),
  M("downloads", "Downloads", "number", "app", "Integer", ["kpi"]),
  M("revenuePerDownload", "Revenue Per Download", "currency", "app", "Currency", ["kpi"]),
  M("revenuePerAppInstall", "Revenue Per App Install", "currency", "app", "Currency", ["kpi"]),
  // Assist (2)
  M("assists", "Assists", "number", "engagement", "Integer", ["kpi", "data-table"]),
  M("costPerAssist", "Cost Per Assist", "currency", "engagement", "Currency", ["kpi"]),
  // Impression Share — Standard (3)
  M("impressionSharePercent", "Impression Share %", "percentage", "impression_share", "Percentage", ["kpi", "gauge", "benchmark"]),
  M("impressionShareLostToBudgetPercent", "Impression Share Lost To Budget %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("impressionShareLostToRankPercent", "Impression Share Lost To Rank %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  // Click Share (1)
  M("clickSharePercent", "Click Share %", "percentage", "impression_share", "Percentage", ["kpi", "gauge", "benchmark"]),
  // Impression Share — Absolute Top (4)
  M("absoluteTopImpressionRatePercent", "Absolute Top Impression Rate %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("absoluteTopImpressionSharePercent", "Absolute Top Impression Share %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("absoluteTopImpressionShareLostToBudgetPercent", "Absolute Top Impression Share Lost To Budget %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("absoluteTopImpressionShareLostToRankPercent", "Absolute Top Impression Share Lost To Rank %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  // Impression Share — Top (4)
  M("topImpressionRatePercent", "Top Impression Rate %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("topImpressionSharePercent", "Top Impression Share %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("topImpressionShareLostToBudgetPercent", "Top Impression Share Lost To Budget %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("topImpressionShareLostToRankPercent", "Top Impression Share Lost To Rank %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  // Audience IS (3)
  M("audienceImpressionSharePercent", "Audience Impression Share %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("audienceImpressionLostToBudgetPercent", "Audience Impression Lost To Budget %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  M("audienceImpressionLostToRankPercent", "Audience Impression Lost To Rank %", "percentage", "impression_share", "Percentage", ["kpi", "gauge"]),
  // Quality Score (9) — search-only, no line-chart per AC #6
  M("qualityScore", "Quality Score", "number", "quality_score", "Integer", ["kpi", "gauge", "data-table"]),
  M("historicalQualityScore", "Historical Quality Score", "number", "quality_score", "Integer", ["kpi", "gauge", "data-table"]),
  M("qualityImpact", "Quality Impact", "number", "quality_score", "Integer", ["kpi", "data-table"]),
  M("expectedCtr", "Expected CTR", "number", "quality_score", "Enum", ["data-table"]),
  M("historicalExpectedCtr", "Historical Expected CTR", "number", "quality_score", "Enum", ["data-table"]),
  M("adRelevance", "Ad Relevance", "number", "quality_score", "Enum", ["data-table"]),
  M("historicalAdRelevance", "Historical Ad Relevance", "number", "quality_score", "Enum", ["data-table"]),
  M("landingPageExperience", "Landing Page Experience", "number", "quality_score", "Enum", ["data-table"]),
  M("historicalLandingPageExperience", "Historical Landing Page Experience", "number", "quality_score", "Enum", ["data-table"]),
  // Shopping benchmarks (2)
  M("benchmarkBid", "Benchmark Bid", "currency", "shopping", "Currency", ["data-table"]),
  M("benchmarkCtr", "Benchmark CTR", "percentage", "shopping", "Percentage", ["benchmark", "data-table"]),
  // Assisted (3)
  M("assistedClicks", "Assisted Clicks", "number", "engagement", "Integer", ["data-table"]),
  M("assistedImpressions", "Assisted Impressions", "number", "engagement", "Integer", ["data-table"]),
  M("assistedConversions", "Assisted Conversions", "number", "conversions", "Integer", ["data-table"]),
  // Call (1)
  M("callDuration", "Call Duration", "duration", "call", "Duration", ["kpi", "data-table"]),
  // Ad elements (3)
  M("extraLogoImpressions", "Extra Logo Impressions", "number", "creative", "Integer", ["data-table"]),
  M("extraLogoClicks", "Extra Logo Clicks", "number", "creative", "Integer", ["data-table"]),
  M("totalClicksOnAdElements", "Total Clicks On Ad Elements", "number", "creative", "Integer", ["data-table"]),
  // Video & DSA (2)
  M("videoDuration", "Video Duration", "duration", "video", "Duration", ["data-table"]),
  M("websiteCoverage", "Website Coverage (DSA)", "number", "dsa", "Decimal", ["data-table"]),
];

// ============================================================================
// Story 31-31 — aggregation annotations (multi-attachment widgets, ADR-0004)
// ============================================================================
// The positional `M(...)` builder cannot carry the new optional fields without
// churning every call signature, so annotations are composed AFTER the base
// catalogue (sanctioned by Story 31-31 AC #4: "extend the M(...) helper or
// compose after"). Every metric key MUST appear below — omissions fail the
// registry-wide gap report in `metric-registry.aggregation.test.ts`.
//   A  = additive (values sum across attached resources)
//   R  = ratio with the natural weight as denominator (weighted average)
//   RU = ratio with NO catalog weight -> UNWEIGHTED_RATIO_ALLOWLIST entry
// Array/Enum-dataType metrics are EXEMPT and intentionally absent: expectedCtr, historicalExpectedCtr, adRelevance, historicalAdRelevance, landingPageExperience, historicalLandingPageExperience.
type AggregationAnnotation = Pick<
  MetricDefinition,
  "aggregationKind" | "ratioComponents"
>;
const A: AggregationAnnotation = { aggregationKind: "additive" };
const RU: AggregationAnnotation = { aggregationKind: "ratio" };
const R = (denominator: string, numerator?: string): AggregationAnnotation => ({
  aggregationKind: "ratio",
  ratioComponents: {
    ...(numerator !== undefined ? { numerator } : {}),
    denominator,
  },
});

const MICROSOFT_ADS_METRICS_AGGREGATION: Readonly<Record<string, AggregationAnnotation>> = {
  impressions: A,
  dailySpend: A,
  clicks: A,
  ctr: R("impressions", "clicks"),
  spend: A,
  averageCpc: R("clicks", "spend"),
  averageCpm: R("impressions", "spend"),
  costPerConversion: R("numberOfConversions", "spend"),
  conversionsQualified: A,
  allConversionsQualified: A,
  conversionRate: R("clicks"),
  allConversionRate: R("clicks"),
  allCostPerConversion: R("allConversionsQualified", "spend"),
  viewThroughConversions: A,
  viewThroughConversionRate: R("impressions", "viewThroughConversions"),
  viewThroughCostPerConversion: R("viewThroughConversions", "spend"),
  numberOfConversions: A,
  returnOnAdSpend: R("spend", "revenue"),
  allReturnOnAdSpend: R("spend", "allRevenue"),
  revenue: A,
  allRevenue: A,
  revenuePerConversion: R("numberOfConversions", "revenue"),
  allRevenuePerConversion: R("allConversionsQualified", "allRevenue"),
  installs: A,
  numberOfInstalls: A,
  downloads: A,
  revenuePerDownload: R("downloads", "revenue"),
  revenuePerAppInstall: R("installs", "revenue"),
  assists: A,
  costPerAssist: R("assists", "spend"),
  impressionSharePercent: RU,
  impressionShareLostToBudgetPercent: RU,
  impressionShareLostToRankPercent: RU,
  clickSharePercent: RU,
  absoluteTopImpressionRatePercent: R("impressions"),
  absoluteTopImpressionSharePercent: RU,
  absoluteTopImpressionShareLostToBudgetPercent: RU,
  absoluteTopImpressionShareLostToRankPercent: RU,
  topImpressionRatePercent: R("impressions"),
  topImpressionSharePercent: RU,
  topImpressionShareLostToBudgetPercent: RU,
  topImpressionShareLostToRankPercent: RU,
  audienceImpressionSharePercent: RU,
  audienceImpressionLostToBudgetPercent: RU,
  audienceImpressionLostToRankPercent: RU,
  qualityScore: RU,
  historicalQualityScore: RU,
  qualityImpact: RU,
  benchmarkBid: RU,
  benchmarkCtr: RU,
  assistedClicks: A,
  assistedImpressions: A,
  assistedConversions: A,
  callDuration: A,
  extraLogoImpressions: A,
  extraLogoClicks: A,
  totalClicksOnAdElements: A,
  videoDuration: RU,
  websiteCoverage: RU,
};

export const MICROSOFT_ADS_METRICS: MetricDefinition[] = MICROSOFT_ADS_METRICS_BASE.map((m) => {
  const agg = MICROSOFT_ADS_METRICS_AGGREGATION[m.key];
  return agg ? { ...m, ...agg } : m;
});


// ============================================================================
// MICROSOFT_ADS_DIMENSIONS — 85 entries pinned to integration-metrics/microsoft-ads.md
// ============================================================================

const D = (
  key: string,
  label: string,
  type: DimensionDefinition["type"],
  exampleValues?: string[]
): DimensionDefinition =>
  exampleValues ? { key, label, type, exampleValues } : { key, label, type };

export const MICROSOFT_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Time (2)
  D("date", "Date / Time Period", "Time", ["2026-04-26"]),
  D("dateTime", "DateTime (Change History)", "Time"),
  // Account & Customer (9)
  D("customerId", "Customer ID", "Structure"),
  D("customerName", "Customer Name", "Structure", ["Contoso"]),
  D("accountId", "Account ID", "Structure"),
  D("accountName", "Account Name", "Structure", ["Contoso US"]),
  D("accountNumber", "Account Number", "Structure"),
  D("accountStatus", "Account Status", "Structure", ["Active", "Paused"]),
  D("currencyCode", "Currency Code", "Structure", ["USD", "EUR", "GBP"]),
  D("baseCampaignId", "Base Campaign ID", "Structure"),
  D("bidStrategyType", "Bid Strategy Type", "Structure", ["MaxClicks", "TargetCpa"]),
  // Campaign (8)
  D("campaignId", "Campaign ID", "Structure"),
  D("campaignName", "Campaign Name", "Structure"),
  D("campaignStatus", "Campaign Status", "Structure", ["Active", "Paused"]),
  D("campaignType", "Campaign Type", "Structure", ["Search", "Audience", "Shopping", "DSA", "PMax"]),
  D("campaignLabels", "Campaign Labels", "Structure"),
  D("budgetName", "Budget Name", "Structure"),
  D("budgetStatus", "Budget Status", "Structure"),
  D("budgetAssociationStatus", "Budget Association Status", "Structure"),
  // Ad Group & Ad (22) — 20-41 in the directive (some rows bundle multiple sub-fields)
  D("adGroupId", "Ad Group ID", "Structure"),
  D("adGroupName", "Ad Group Name", "Structure"),
  D("adGroupStatus", "Ad Group Status", "Structure"),
  D("adGroupType", "Ad Group Type", "Structure"),
  D("adGroupLabels", "Ad Group Labels", "Structure"),
  D("adId", "Ad ID", "Structure"),
  D("adStatus", "Ad Status", "Structure"),
  D("adType", "Ad Type", "Structure"),
  D("adTitle", "Ad Title", "Creative"),
  D("adDescription", "Ad Description", "Creative"),
  D("adDescription2", "Ad Description 2", "Creative"),
  D("adLabels", "Ad Labels", "Structure"),
  D("displayUrl", "Display URL", "Creative"),
  D("destinationUrl", "Destination URL", "Creative"),
  D("finalUrl", "Final URL", "Creative"),
  D("finalMobileUrl", "Final Mobile URL", "Creative"),
  D("trackingTemplate", "Tracking Template", "Creative"),
  D("customParameters", "Custom Parameters", "Creative"),
  D("headlines", "Headline 1 / 2 / 3", "Creative"),
  D("descriptions", "Description 1 / 2", "Creative"),
  D("longHeadline", "Long Headline (Audience)", "Creative"),
  D("paths", "Path 1 / Path 2", "Creative"),
  // Keyword & Negative (10)
  D("keyword", "Keyword", "Search"),
  D("keywordId", "Keyword ID", "Search"),
  D("keywordStatus", "Keyword Status", "Search"),
  D("keywordLabels", "Keyword Labels", "Structure"),
  D("bidMatchType", "Bid Match Type", "Search", ["Exact", "Phrase", "Broad"]),
  D("deliveredMatchType", "Delivered Match Type", "Search"),
  D("currentMaxCpc", "Current Max CPC", "Search"),
  D("searchQuery", "Search Query", "Search"),
  D("negativeKeyword", "Negative Keyword", "Search"),
  D("negativeKeywordMatchType", "Negative Keyword Match Type", "Search"),
  // Asset Group / PMax (4)
  D("assetGroupId", "Asset Group ID", "Structure"),
  D("assetGroupName", "Asset Group Name", "Structure"),
  D("assetGroupStatus", "Asset Group Status", "Structure"),
  D("adGroupCriterionId", "Ad Group Criterion ID", "Structure"),
  // Audience & Demographics (6)
  D("audience", "Audience ID / Name", "Audience"),
  D("ageGroup", "Age Group", "Demo", ["18-24", "25-34", "35-49"]),
  D("gender", "Gender", "Demo", ["Male", "Female", "Unknown"]),
  D("companyName", "Company Name (LinkedIn Targeting)", "Audience"),
  D("industryName", "Industry Name (LinkedIn Targeting)", "Audience"),
  D("jobFunctionName", "Job Function Name (LinkedIn Targeting)", "Audience"),
  // Ad Extensions (5)
  D("adExtensionId", "Ad Extension ID", "Structure"),
  D("adExtensionType", "Ad Extension Type", "Structure"),
  D("adExtensionVersion", "Ad Extension Version", "Structure"),
  D("clickType", "Click Type", "Action"),
  D("callTime", "Call Start / End Time", "Time"),
  // Shopping / Product (9 directive rows 67-75; bundles flattened per AC #6)
  D("merchantProductId", "Merchant Product ID", "Product"),
  D("sellerName", "Seller Name", "Product"),
  D("countryOfSale", "Country Of Sale", "Geo"),
  D("gtin", "GTIN", "Product"),
  D("mpn", "MPN", "Product"),
  D("productGroup", "Product Group", "Product"),
  // Bundle-row: directive #73 covers ProductCategory1..5; sub-field expansion
  // happens at query-build time (report-builders.ts), not in the registry.
  D("productCategory", "Product Category 1–5", "Product"),
  // Bundle-row: directive #74 covers ProductType1..5.
  D("productType", "Product Type 1–5", "Product"),
  // Bundle-row: directive #75 covers CustomLabel0..4.
  D("customLabel", "Custom Label 0–4", "Product"),
  // DSA (3)
  D("dynamicAdTarget", "Dynamic Ad Target", "Search"),
  D("dynamicAdTargetStatus", "Dynamic Ad Target Status", "Search"),
  D("dsaCategoryList", "DSA Category List", "Search"),
  // Geography (8) — 79-85 in directive (one row bundles QueryIntent sub-fields)
  D("country", "Country", "Geo", ["United States", "Canada", "Germany"]),
  D("state", "State / Region", "Geo"),
  D("city", "City", "Geo"),
  D("metroArea", "Metro Area (DMA)", "Geo"),
  D("postalCode", "Postal Code", "Geo"),
  D("locationType", "Location Type (Physical vs Interest)", "Geo", ["Physical", "Interest"]),
  D("queryIntentLocation", "Query Intent Location", "Geo"),
  // Device, Network & Publisher (7)
  D("deviceType", "Device Type", "Tech", ["Computer", "Smartphone", "Tablet"]),
  D("deviceOs", "Device OS", "Tech", ["Windows", "iOS", "Android"]),
  D("network", "Network", "Delivery", ["Bing", "Yahoo", "Syndicated"]),
  D("adDistribution", "Ad Distribution", "Delivery", ["Search", "Audience"]),
  D("language", "Language", "Tech", ["English", "Spanish", "French"]),
  D("publisherUrl", "Publisher URL", "Delivery"),
  D("topVsOther", "Top Vs Other", "Delivery", ["Top", "Other"]),
  // Conversion Goals (3)
  D("goal", "Goal", "Conversion"),
  D("goalId", "Goal ID", "Conversion"),
  D("goalType", "Goal Type", "Conversion"),
  // Change History (5) — directive rows 96-100, SearchCampaignChangeHistory report
  D("howChanged", "How Changed", "Action", ["Add", "Edit", "Delete"]),
  D("itemChanged", "Item Changed", "Action"),
  D("attributeChanged", "Attribute Changed", "Action"),
  // Bundle-row: directive #99 covers OldValue/NewValue together.
  D("oldNewValue", "Old Value / New Value", "Action"),
  D("changedBy", "Changed By", "Action"),
];

// ============================================================================
// Adapter Implementation
// ============================================================================

export const microsoftAdsAdapter: IntegrationAdapter = {
  platform: "microsoft-ads",

  rateLimits: {
    // Microsoft's published ceiling is 12,000 calls/min/account; we set the
    // adapter's per-minute hint conservatively so the queue consumer's
    // throttler stays safely under that. ADR-018.
    requestsPerMinute: 60,
    retryAfterMs: 1000,
  },

  /**
   * Fetches metrics for the connected Microsoft Ads account by running the
   * 8 Reporting v13 report types in parallel (Submit→Poll→Download→Unzip→Parse).
   *
   * Failures are classified:
   *   - Fatal codes (token_expired, permission_denied) abort the entire fetch.
   *   - Non-fatal codes (e.g. shopping report on a non-shopping account) emit
   *     an empty `records` slice for that report type plus a warnings entry.
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // ---------- 1. Composite account_id parsing (AC #1, #9) ------------------
    const composite = ctx.accountId ?? "";
    const match = /^([1-9]\d*):([1-9]\d*)$/.exec(composite);
    if (!match) {
      return adapterErr({
        code: "account_not_found",
        message:
          "Microsoft Ads account ID must be in '{customerId}:{accountId}' format with non-zero integer parts",
        retryable: false,
      });
    }
    const customerId = match[1] ?? "";
    const accountId = match[2] ?? "";

    // ---------- 2. Developer token presence (AC #1) --------------------------
    const env = ctx.env as unknown as Record<string, string | undefined>;
    const developerToken = env.MICROSOFT_ADS_DEVELOPER_TOKEN ?? "";
    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message:
          "Missing MICROSOFT_ADS_DEVELOPER_TOKEN env binding — cannot fetch Microsoft Ads metrics",
        retryable: false,
      });
    }

    // ---------- 3. Fixture mode (Story 19.3 AC #11) --------------------------
    // Demo workspaces (`ctx.fixtures === true`) bypass the live Reporting v13
    // pipeline entirely. We read from KV under `fixture:microsoft-ads:{slug}`
    // (the FDL-3 pattern) and slice to the requested date range. `safeFetch`
    // is never invoked — verified by `vi.spyOn` in the adapter tests.
    //
    // Demo composite IDs are decorative — the fixture-mode branch returns
    // before any composite-ID-parsing reaches the Microsoft API. They still
    // pass the same regex above as a defense-in-depth check (AD-19.3-04).
    const fixtures = (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixtures) {
      return loadMicrosoftAdsFixture(ctx, dateRange);
    }

    // ---------- 4. Date range validation (AC #1, Pre-mortem F2) --------------
    if (!(dateRange.start instanceof Date) || !(dateRange.end instanceof Date)) {
      return adapterErr({
        code: "invalid_response",
        message: "Microsoft Ads dateRange.start and dateRange.end must be Date objects",
        retryable: false,
      });
    }
    if (Number.isNaN(dateRange.start.getTime()) || Number.isNaN(dateRange.end.getTime())) {
      return adapterErr({
        code: "invalid_response",
        message: "Microsoft Ads dateRange contains invalid Date values",
        retryable: false,
      });
    }
    if (dateRange.start.getTime() > dateRange.end.getTime()) {
      return adapterErr({
        code: "invalid_response",
        message: "Microsoft Ads dateRange.start must be on or before dateRange.end",
        retryable: false,
      });
    }
    const days = Math.ceil(
      (dateRange.end.getTime() - dateRange.start.getTime()) / (24 * 60 * 60 * 1000)
    ) + 1;
    if (days > MAX_FETCHMETRICS_DAYS) {
      return adapterErr({
        code: "invalid_response",
        message: `Microsoft Ads fetchMetrics is capped at ${MAX_FETCHMETRICS_DAYS} days; split larger ranges in the queue consumer`,
        retryable: false,
      });
    }
    for (const reportType of REPORT_TYPES) {
      const cap = MAX_DATE_RANGE_DAYS[reportType];
      if (days > cap) {
        return adapterErr({
          code: "invalid_response",
          message: `Microsoft Ads ${reportType} report only accepts up to ${cap} days; got ${days}`,
          retryable: false,
        });
      }
    }

    // ---------- 5. Run 8 reports in parallel ---------------------------------
    type PipelineSuccess = {
      ok: true;
      reportType: MicrosoftAdsReportType;
      records: Array<Record<string, unknown>>;
    };
    type PipelineWarning = {
      ok: false;
      fatal: false;
      reportType: MicrosoftAdsReportType;
      reason: string;
    };
    type PipelineFatal = {
      ok: false;
      fatal: true;
      reportType: MicrosoftAdsReportType;
      code: AdapterErrorCode;
      message: string;
      retryable: boolean;
      retryAfterMs?: number;
    };
    type PipelineResult = PipelineSuccess | PipelineWarning | PipelineFatal;

    const pipelines: Array<Promise<PipelineResult>> = REPORT_TYPES.map(
      (reportType) =>
        runReportPipeline({
          reportType,
          accessToken: credentials.accessToken,
          developerToken,
          customerId,
          accountId,
          dateRange,
        })
    );

    const settled = await Promise.all(pipelines);

    // ---------- 6. Classify fatal vs warning ---------------------------------
    // Prefer user-actionable fatal errors (re-auth required) over retryable
    // ones — if any pipeline says token_expired, that's the most useful signal
    // to surface even if other pipelines also rate-limited at the same time.
    const allFatals = settled.filter(
      (r): r is PipelineFatal => !r.ok && r.fatal
    );
    if (allFatals.length > 0) {
      const PRIORITY: AdapterErrorCode[] = [
        "token_expired",
        "token_invalid",
        "permission_denied",
        "api_not_enabled",
        "rate_limited",
        "provider_unavailable",
      ];
      const sorted = [...allFatals].sort(
        (a, b) => PRIORITY.indexOf(a.code) - PRIORITY.indexOf(b.code)
      );
      const fatal = sorted[0]!;
      return adapterErr({
        code: fatal.code,
        message: fatal.message,
        retryable: fatal.retryable,
        retryAfterMs: fatal.retryAfterMs,
      });
    }

    const warnings: Array<{ reportType: string; reason: string }> = [];
    const records: Array<Record<string, unknown>> = [];
    for (const r of settled) {
      if (r.ok) {
        // Static contract check (Pre-mortem F4) — every record must carry a
        // non-empty _reportType discriminator.
        for (const rec of r.records) {
          if (
            !rec._reportType ||
            typeof rec._reportType !== "string"
          ) {
            return adapterErr({
              code: "invalid_response",
              message: "Microsoft Ads transformer emitted a record without _reportType",
              retryable: false,
            });
          }
        }
        records.push(...r.records);
      } else if (!r.fatal) {
        warnings.push({ reportType: r.reportType, reason: r.reason });
      }
      // Fatal pipelines were already drained in the `fatal` short-circuit above.
    }

    // ---------- 7. Compose AdapterMetrics ------------------------------------
    const metrics: AdapterMetrics = {
      platform: "microsoft-ads",
      accountId: composite,
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        customerId,
        accountId,
        // currencyCode/timeZone are propagated when the integration's metadata
        // pre-loaded them (Story 19.1's User/Query stash). When absent, widgets
        // fall back to USD/Pacific defaults.
        currencyCode: extractCurrencyCode(records) ?? undefined,
        timeZone: "PacificTimeUSCanadaTijuana",
      },
    };
    return adapterOk(metrics);
  },

  /**
   * Validates the connection by re-running `GET /User/Query` with the Bearer
   * token + DeveloperToken. A successful response with at least one customer
   * role indicates a healthy connection.
   */
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const env = ctx.env as unknown as Record<string, string | undefined>;
    const developerToken = env.MICROSOFT_ADS_DEVELOPER_TOKEN ?? "";
    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message:
          "Missing MICROSOFT_ADS_DEVELOPER_TOKEN env binding — cannot validate Microsoft Ads connection",
        retryable: false,
      });
    }

    try {
      const userInfo = await fetchMicrosoftAdsUserInfo(
        credentials.accessToken,
        developerToken
      );
      const hasUsableAccount = userInfo.customerRoles.some(
        (r) => r.accountIds.length > 0
      );
      if (!hasUsableAccount) {
        return adapterErr({
          code: "account_not_found",
          message:
            "Microsoft account is not linked to any Microsoft Advertising account",
          retryable: false,
        });
      }
      return adapterOk(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(
          `Microsoft Ads connection validation failed: ${message}`
        ),
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  async refreshToken(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Microsoft Ads",
        retryable: false,
      });
    }

    const env = ctx.env as unknown as Record<string, string | undefined>;
    const clientId = env.MICROSOFT_ADS_CLIENT_ID ?? "";
    const clientSecret = env.MICROSOFT_ADS_CLIENT_SECRET ?? "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message:
          "Missing MICROSOFT_ADS_CLIENT_ID or MICROSOFT_ADS_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshMicrosoftAdsToken(
        credentials.refreshToken,
        clientId,
        clientSecret
      );

      // Task 2.16 / TD20: Microsoft rotates the refresh token only when it
      // returns a NEW `refresh_token` value. If absent, the existing DB
      // refresh-token is preserved (omitUndefined drops the key); we leave
      // refreshTokenExpiresAt undefined too so the existing DB value stays.
      const refreshTokenExpiresAt = tokenResponse.refresh_token
        ? new Date(Date.now() + MICROSOFT_ADS_REFRESH_TOKEN_LIFETIME_MS)
        : undefined;
      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken:
          tokenResponse.refresh_token ?? credentials.refreshToken,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
        refreshTokenExpiresAt,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isInvalidGrant = /invalid_grant/i.test(message);
      return adapterErr({
        code: isInvalidGrant ? "token_expired" : "token_invalid",
        message: sanitizeErrorMessage(
          `Microsoft Ads token refresh failed: ${message}`
        ),
        retryable: false,
      });
    }
  },

  /**
   * Catalog discovery (Story 31-12).
   *
   * ## Single-tenant classification vs full-catalog discovery (31-12 AC #2)
   *
   * The registry entry (`registry.ts:175`, Story 31-5) classifies Microsoft
   * Ads as `oauthGrantScope: "single-tenant"` — that is the V1-EFFECTIVE
   * grant/auto-attach treatment per the 31-12 demotion decision, NOT a
   * statement about what this user can see. The classification governs how
   * many resources a grant auto-attaches; it does not license a partial
   * catalog. This method therefore intentionally returns the FULL
   * (customerId × accountId) catalog — owned AND agency-linked accounts —
   * as promotion forward-compat: when the registry's PROMOTION CRITERIA
   * fire (3+ paying customers request multiple MS Ads accounts), promotion
   * is a registry-field flip + attach-UI work, with the catalog in
   * `oauth_grant_resources` already complete.
   *
   * The deterministic primary (customerId, accountId) pick lives in the
   * OAuth callback (`apps/server/src/routes/integrations/microsoft-ads-oauth.ts`),
   * NOT here.
   *
   * ## Composite id (31-12 AC #3 — FROZEN)
   *
   * `id` is exactly `${customerId}:${accountId}`. It matches
   * `buildMicrosoftAdsCacheKey`'s axes and the rows already upserted by the
   * 31-3 backfill — changing the format orphans every existing
   * `oauth_grant_resources` row for this platform. (The sprint-status stub's
   * `externalAccountId: customer_id` claim is stale; this is the ground truth.)
   *
   * ## Name enrichment is best-effort, membership is User/Query-only (AC #6)
   *
   * `User/Query` is the SOLE source of catalog membership. `Accounts/Search`
   * may only improve display names; any per-customer search failure (or an
   * account missing from the search page) falls back to the synthetic
   * `Account ${accountId}` name — never drops an entry.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const env = ctx.env as unknown as Record<string, string | undefined>;
    const developerToken = env.MICROSOFT_ADS_DEVELOPER_TOKEN ?? "";
    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message:
          "Missing MICROSOFT_ADS_DEVELOPER_TOKEN env binding — cannot discover Microsoft Ads accounts",
        retryable: false,
      });
    }

    try {
      const userInfo = await fetchMicrosoftAdsUserInfo(
        credentials.accessToken,
        developerToken
      );

      // Defensive dedupe by composite id (ADR-0004 §Anti-Patterns 4): the DB
      // UNIQUE index is the backstop, not the first line. First occurrence
      // wins, except an owned (`AccountIds`) occurrence always replaces a
      // linked one for the same composite id (31-12 AC #4).
      const accountsById = new Map<string, AccountInfo>();

      for (const role of userInfo.customerRoles) {
        const ownedIds = role.accountIds;
        const linkedIds = role.linkedAccountIds ?? [];

        // Best-effort real-name enrichment (AC #6). NEVER filters membership:
        // an account absent from the search result keeps its synthetic name.
        //
        // 31-12 code review — known limitation: `Accounts/Search` is scoped
        // `ParentCustomerId Equals <this customerId>`, and an agency-LINKED
        // account's ParentCustomerId is the OWNING (client) customer, not this
        // one. In production, linked accounts therefore typically stay on the
        // synthetic `Account ${id}` name (names-only degradation — exactly the
        // failure mode AC #6a allows). We still pass linked ids in the wanted
        // set so any future API behavior that DOES surface them enriches for
        // free; enriching them properly would cost one `AccountId Equals`
        // round-trip per linked account and is deferred until the attach UI
        // (post-PROMOTION) needs real linked names.
        const nameByAccountId = new Map<string, string>();
        const allIds = [...new Set([...ownedIds, ...linkedIds])];
        if (allIds.length > 0) {
          try {
            const advertiserAccounts = await fetchMicrosoftAdsAccountsSearch(
              credentials.accessToken,
              developerToken,
              role.customerId,
              allIds
            );
            for (const acct of advertiserAccounts) {
              if (acct.accountName) {
                nameByAccountId.set(acct.accountId, acct.accountName);
              }
            }
          } catch (err) {
            // Non-fatal by contract (AC #6b): log and fall back to synthetic
            // names for THIS customer only; discovery still succeeds.
            const message = err instanceof Error ? err.message : String(err);
            console.warn(
              sanitizeErrorMessage(
                `Microsoft Ads Accounts/Search name enrichment failed for customer ${role.customerId} — using synthetic names: ${message}`
              )
            );
          }
        }

        const buildEntry = (accountId: string, linked: boolean): AccountInfo => {
          const accountName = nameByAccountId.get(accountId);
          const displayName = accountName ?? `Account ${accountId}`;
          return {
            // AC #3: composite id frozen as `${customerId}:${accountId}`.
            id: `${role.customerId}:${accountId}`,
            name: role.customerName
              ? `${role.customerName} • ${displayName}`
              : displayName,
            platform: "microsoft-ads",
            // AC #1: display/consent metadata ONLY (31-4 contract), sourced
            // from the single source of truth. Fresh array per entry so no
            // caller can mutate a shared reference into the catalog upsert.
            scopes: [...MICROSOFT_ADS_SCOPES],
            metadata: {
              customerId: role.customerId,
              customerName: role.customerName,
              accountId,
              linked,
              ...(accountName !== undefined ? { accountName } : {}),
            },
          };
        };

        for (const accountId of ownedIds) {
          const id = `${role.customerId}:${accountId}`;
          const existing = accountsById.get(id);
          // Owned wins over linked even when the linked occurrence (from an
          // earlier role echo) was inserted first.
          if (!existing || existing.metadata?.linked === true) {
            accountsById.set(id, buildEntry(accountId, false));
          }
        }
        for (const accountId of linkedIds) {
          const id = `${role.customerId}:${accountId}`;
          if (!accountsById.has(id)) {
            accountsById.set(id, buildEntry(accountId, true));
          }
        }
      }

      // Empty catalog (zero roles, or roles with zero accounts) is a VALID
      // success per the 31-4 contract — deliberately NOT validateConnection's
      // `account_not_found` semantics (AC #7).
      return adapterOk([...accountsById.values()]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const code = adapterErrorCodeFromError(err);
      return adapterErr({
        code,
        message: sanitizeErrorMessage(
          `Microsoft Ads account discovery failed: ${message}`
        ),
        // AC #8: mirror validateConnection's retryability instead of a
        // hardcoded false — rate limits and network blips are retryable.
        retryable: code === "rate_limited" || code === "network_error",
      });
    }
  },

  getAvailableMetrics(): MetricDefinition[] {
    return MICROSOFT_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return MICROSOFT_ADS_DIMENSIONS;
  },
};

/** API version is exported for parity with other adapters' module-level constants. */
export const MICROSOFT_ADS_API_VERSION = API_VERSIONS.microsoftAds;

/**
 * Builds the canonical KV cache key shape for Microsoft Ads metrics fetches.
 *
 * Format (AC #11): `microsoft-ads:{tenantId}:{customerId}:{accountId}:{startISO}:{endISO}`
 *
 * The single key intentionally covers all 8 reports so they always cache or
 * invalidate atomically. The `tenantId` prefix ensures cache isolation even if
 * two tenants share a Microsoft customer/account (Reviewer A security concern).
 *
 * The route layer / queue consumer wraps `microsoftAdsAdapter.fetchMetrics`
 * with `fetchMetricsWithCache(kv, buildMicrosoftAdsCacheKey(...), "microsoft-ads", tenantId, ...)`.
 * Adapter-internal cache wrapping would couple the adapter to KV semantics —
 * we deliberately keep that at the call site (consistent with all other adapters).
 */
export function buildMicrosoftAdsCacheKey(
  tenantId: string,
  customerId: string,
  accountId: string,
  dateRange: { start: Date; end: Date }
): string {
  const startIso = dateRange.start.toISOString().slice(0, 10);
  const endIso = dateRange.end.toISOString().slice(0, 10);
  return `microsoft-ads:${tenantId}:${customerId}:${accountId}:${startIso}:${endIso}`;
}

// ============================================================================
// Pipeline runner
// ============================================================================

interface PipelineInput {
  reportType: MicrosoftAdsReportType;
  accessToken: string;
  developerToken: string;
  customerId: string;
  accountId: string;
  dateRange: { start: Date; end: Date };
}

/**
 * Runs the Submit→Poll→Download→Unzip→Parse lifecycle for one report type and
 * returns a discriminated result the caller fans into the final
 * AdapterMetrics.
 */
async function runReportPipeline(
  input: PipelineInput
): Promise<
  | { ok: true; reportType: MicrosoftAdsReportType; records: Array<Record<string, unknown>> }
  | { ok: false; fatal: false; reportType: MicrosoftAdsReportType; reason: string }
  | {
      ok: false;
      fatal: true;
      reportType: MicrosoftAdsReportType;
      code: AdapterErrorCode;
      message: string;
      retryable: boolean;
      retryAfterMs?: number;
    }
> {
  const apiReportType = REPORT_TYPE_TO_API[input.reportType];
  if (!apiReportType) {
    return {
      ok: false,
      fatal: true,
      reportType: input.reportType,
      code: "invalid_response",
      message: `Microsoft Ads runReportPipeline: unknown reportType "${input.reportType}"`,
      retryable: false,
    };
  }

  // Defence-in-depth — verify the column list exists; module-load already
  // asserts it but a future runtime mutation would surface here.
  if (!COLUMNS_BY_REPORT_TYPE[input.reportType]) {
    return {
      ok: false,
      fatal: true,
      reportType: input.reportType,
      code: "invalid_response",
      message: `Microsoft Ads runReportPipeline: missing columns for "${input.reportType}"`,
      retryable: false,
    };
  }

  const body = buildSubmitBody({
    reportType: input.reportType,
    apiReportType,
    accountId: input.accountId,
    dateRange: input.dateRange,
  });

  try {
    // Submit
    const submitted = await submitMicrosoftAdsReport(
      input.accessToken,
      input.developerToken,
      input.customerId,
      input.accountId,
      body
    );
    // Poll → ReportDownloadUrl
    let csvText: string;
    try {
      const url = await pollMicrosoftAdsReportUntilDone(
        input.accessToken,
        input.developerToken,
        input.customerId,
        submitted.ReportRequestId,
        { accountId: input.accountId }
      );
      csvText = await downloadAndExtractCsv(url);
    } catch (downloadErr) {
      // Stale URL retry: if the signed download URL expired, re-poll once for
      // a fresh one. Microsoft surfaces this as 403 — we treat any
      // permission_denied during *download* (after a successful poll) as a
      // signal to re-poll. After the second attempt, we surface the original
      // error.
      if (
        downloadErr instanceof MicrosoftAdsApiCallError &&
        downloadErr.type === "permission_denied"
      ) {
        try {
          const freshUrl = await pollMicrosoftAdsReportUntilDone(
            input.accessToken,
            input.developerToken,
            input.customerId,
            submitted.ReportRequestId,
            { accountId: input.accountId }
          );
          csvText = await downloadAndExtractCsv(freshUrl);
        } catch {
          throw downloadErr;
        }
      } else {
        throw downloadErr;
      }
    }
    const rawRows = parseMicrosoftAdsCsv(csvText, input.reportType);
    const transformer = TRANSFORMERS[input.reportType];
    const records = transformer(rawRows) as Array<Record<string, unknown>>;
    return {
      ok: true,
      reportType: input.reportType,
      records,
    };
  } catch (err) {
    // Classify the error.
    if (err instanceof MicrosoftAdsApiCallError) {
      const code = microsoftAdsErrorTypeToAdapterCode(err.type);
      if (FATAL_ERROR_CODES.has(code)) {
        return {
          ok: false,
          fatal: true,
          reportType: input.reportType,
          code,
          message: sanitizeErrorMessage(
            `Microsoft Ads ${input.reportType} report failed: ${err.message}`
          ),
          retryable: err.retryable,
        };
      }
      // Non-fatal: shopping on non-shopping account, demographics under
      // threshold, etc. Emit a warning, return empty records.
      return {
        ok: false,
        fatal: false,
        reportType: input.reportType,
        reason: sanitizeErrorMessage(err.message),
      };
    }
    // Unknown error — treat as non-fatal (best effort) but log via reason so
    // an oncall engineer can debug from the warnings array.
    return {
      ok: false,
      fatal: false,
      reportType: input.reportType,
      reason: sanitizeErrorMessage(
        err instanceof Error ? err.message : String(err)
      ),
    };
  }
}

/**
 * Pull the first non-empty `currencyCode` we see across all records. Microsoft
 * stamps it on every row of every report type, so any record will do.
 */
function extractCurrencyCode(
  records: ReadonlyArray<Record<string, unknown>>
): string | null {
  for (const r of records) {
    const code = r.currencyCode;
    if (typeof code === "string" && code.trim() !== "") {
      return code;
    }
  }
  return null;
}

// ============================================================================
// Fixture-mode Helper (Story 19.3, AC #11)
// ============================================================================

/**
 * Load fixture data for a demo-mode Microsoft Ads integration.
 *
 * Mirrors the `fetchDemoMetrics` pattern from FDL-3 used by every other
 * adapter that supports demo mode. KV-only — no HTTP. Rate-limited by the
 * existing per-route limiter (Story 1.11 — applies to `/api/clients/*`
 * uniformly; demo workspaces inherit this).
 *
 * Implementation note (Round 1 review L2): KV/D1 helpers from `../fixtures/`
 * are loaded via dynamic `import(...)` so that live-mode callers don't pay
 * the cost of pulling fixture-only modules into their bundle. The
 * trade-off: a missing fixtures sub-package surfaces only when the
 * fixture-mode branch first runs, not at module load. In production we
 * never bundle without `../fixtures/`, so this is safe.
 *
 * Returns:
 *   - `adapterOk(metrics)` on KV hit (records sliced to dateRange)
 *   - `adapterErr({ code: "account_not_found", ... })` on KV miss with a
 *     message naming the `pnpm fixtures:upload` runbook.
 */
async function loadMicrosoftAdsFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  // Resolve the demo client slug from the database (tenant + client).
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Microsoft Ads demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve demo client slug",
      retryable: false,
    });
  }

  // Lazy import — the KV/D1 helpers live in the fixtures sub-package and
  // shouldn't be hot-loaded for live calls.
  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Microsoft Ads demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "microsoft-ads",
    clientSlug
  );

  if (metrics === null) {
    // KV miss: adapter contract has no bespoke `fixture_not_available` code,
    // so we surface as `account_not_found` (consistent with the live path's
    // "the resource you asked about does not exist" semantic) plus an
    // actionable message that names the runbook. Adding a new code would
    // require touching every adapter — round 1 review M3.
    return adapterErr({
      code: "account_not_found",
      message: `Microsoft Ads demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(metrics, dateRange.start, dateRange.end);
  return adapterOk(filtered);
}

// ============================================================================
// Error Code Inference
// ============================================================================

const NETWORK_ERROR_MESSAGE_RE = /timed out|Network error|fetch failed/i;

function adapterErrorCodeFromError(err: unknown): AdapterErrorCode {
  const message = err instanceof Error ? err.message : String(err);
  if (err instanceof MicrosoftAdsApiCallError) {
    // Story 31-12 AC #8: a transport-level failure (fetch threw / timed out)
    // reaches us as a SYNTHESISED "unknown"-type MicrosoftAdsApiCallError
    // (buildMicrosoftAdsApiError always wraps safeFetch errors). Without this
    // check the instanceof branch short-circuits the network regex below and
    // network blips map to non-retryable invalid_response. Map them to
    // network_error so the shared retryability formula
    // (`rate_limited || network_error`) treats them as retryable.
    //
    // 31-12 code review: the reclassification additionally requires
    // `status == null`. Transport failures are the ONLY synthesised errors
    // without an HTTP status (safeFetch sets providerStatus on every HTTP
    // error path); a provider-answered 4xx whose body merely contains
    // "timed out" must stay non-retryable invalid_response — Microsoft
    // responded, so the network is fine.
    if (
      err.type === "unknown" &&
      err.status == null &&
      NETWORK_ERROR_MESSAGE_RE.test(message)
    ) {
      return "network_error";
    }
    return microsoftAdsErrorTypeToAdapterCode(err.type);
  }
  if (NETWORK_ERROR_MESSAGE_RE.test(message)) {
    return "network_error";
  }
  return "invalid_response";
}
